import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PropertyFilters } from '../types/property';
import {
  buildSearchParamsFromFilters,
  cityOptions,
  neighborhoodOptions,
  parseFiltersFromSearchParams,
  setLocationFilterOptions,
  typeOptions,
} from '../lib/propertySearch';
import Input from './ui/Input';
import Select from './ui/Select';
import AutocompleteInput from './ui/AutocompleteInput';
import Button from './ui/Button';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { fetchPropertyFilterOptions } from '../services/api';
import FilterModal from './FilterModal';

const transactionOptions: Array<{
  value?: 'venda' | 'aluguel';
  label: string;
}> = [
  { value: undefined, label: 'Todos' },
  { value: 'venda', label: 'Comprar' },
  { value: 'aluguel', label: 'Alugar' },
];

function countAdvancedFilters(filters: PropertyFilters): number {
  let count = 0;
  if (filters.purpose) count += 1;
  if (filters.types && filters.types.length > 0) count += filters.types.length;
  if (filters.bedrooms) count += 1;
  if (filters.suites) count += 1;
  if (filters.parkingSpaces) count += 1;
  if (filters.priceMin) count += 1;
  if (filters.priceMax) count += 1;
  return count;
}

export default function SearchBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [localFilters, setLocalFilters] = useState<PropertyFilters>(() => parseFiltersFromSearchParams(searchParams));
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  useEffect(() => {
    setLocalFilters(parseFiltersFromSearchParams(searchParams));
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    fetchPropertyFilterOptions().then((options) => {
      if (cancelled) {
        return;
      }

      setLocationFilterOptions(options);
      setLocalFilters(parseFiltersFromSearchParams(searchParams));
    });

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const advancedCount = useMemo(() => countAdvancedFilters(localFilters), [localFilters]);

  const navigateWithFilters = (filters: PropertyFilters) => {
    const nextParams = buildSearchParamsFromFilters(filters);
    const search = nextParams.toString();

    navigate({
      pathname: '/imoveis',
      search: search ? `?${search}` : '',
    });
  };

  const handleSearch = () => {
    navigateWithFilters(localFilters);
  };

  const handleReset = () => {
    const defaultFilters: PropertyFilters = {};
    setLocalFilters(defaultFilters);

    if (location.pathname === '/imoveis') {
      navigate('/imoveis');
    }
  };

  const handleApplyAdvanced = (filters: PropertyFilters) => {
    setLocalFilters(filters);
    setIsFilterModalOpen(false);
    navigateWithFilters(filters);
  };

  return (
    <div className="bg-white rounded-sm shadow-lg p-8">
      <div className="mb-6 flex flex-wrap gap-3">
        {transactionOptions.map((option) => {
          const isActive = localFilters.transaction === option.value;

          return (
            <button
              key={option.label}
              type="button"
              onClick={() => setLocalFilters({ ...localFilters, transaction: option.value })}
              className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? 'border-jungle-600 bg-jungle-600 text-white'
                  : 'border-gray-300 text-gray-600 hover:border-forest-900 hover:text-forest-900'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <Input
          placeholder="COD. Referência"
          value={localFilters.code || ''}
          onChange={(e) => setLocalFilters({ ...localFilters, code: e.target.value })}
        />

        <AutocompleteInput
          placeholder="Cidade"
          value={localFilters.city || ''}
          options={cityOptions.slice(1).map((option) => option.label)}
          onChange={(value) => setLocalFilters({ ...localFilters, city: value || undefined })}
        />

        <AutocompleteInput
          placeholder="Bairro"
          value={localFilters.neighborhood || ''}
          options={neighborhoodOptions.slice(1).map((option) => option.label)}
          onChange={(value) => setLocalFilters({ ...localFilters, neighborhood: value || undefined })}
        />

        <Select
          options={typeOptions}
          value={localFilters.type || ''}
          onChange={(e) => setLocalFilters({ ...localFilters, type: e.target.value })}
        />
      </div>

      <div className="flex flex-wrap gap-4">
        <Button onClick={handleSearch} className="flex-1 min-w-[160px] flex items-center justify-center gap-2">
          <Search className="w-5 h-5" />
          BUSCAR
        </Button>
        <Button
          onClick={() => setIsFilterModalOpen(true)}
          variant="outline"
          className="flex items-center gap-2"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Todos os filtros
          {advancedCount > 0 && (
            <span className="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-forest-900 px-1.5 text-xs font-semibold text-white">
              {advancedCount}
            </span>
          )}
        </Button>
        <Button onClick={handleReset} variant="outline" className="flex items-center gap-2">
          <X className="w-4 h-4" />
          Limpar filtros
        </Button>
      </div>

      <FilterModal
        isOpen={isFilterModalOpen}
        initialFilters={localFilters}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={handleApplyAdvanced}
      />
    </div>
  );
}
